import { get } from '@ember/object';
import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import { ChannelType } from 'cio/models/newsletter';
import SegmentModel from 'cio/models/segment';
import { getModelEntryNameFromId } from 'cio/utils/analytics/transforms/utils';
import { isPresent } from '@ember/utils';

type BroadcastEditType =
  | 'channel_set'
  | 'channel_changed'
  | 'tz_disabled_hovered'
  | 'specific'
  | 'user'
  | 'include_in_message_limit_enabled'
  | 'include_in_message_limit_disabled';

type BroadcastSortOption = 'created' | 'name' | 'sent' | 'totalSent' | 'updated';

const SECONDS_TO_DATE_UNIT: Record<number, string> = {
  60: 'Per minute',
  3600: 'Per hour',
  86400: 'Per day',
};

const BROADCAST_EDIT_TYPES: Record<BroadcastEditType, string> = {
  channel_set: 'Newsletter Channel Set',
  channel_changed: 'Newsletter Channel Changed',
  tz_disabled_hovered: "User's Timezone Disabled/Hovered",
  specific: 'Specific Timezone Selected',
  user: "User's Timezone Selected",
  include_in_message_limit_enabled: 'Include in Message Limit Enabled',
  include_in_message_limit_disabled: 'Include in Message Limit Disabled',
};

const BROADCAST_SORT_OPTIONS: Record<BroadcastSortOption, string> = {
  created: 'Date Created',
  name: 'Name',
  sent: 'Date Sent',
  totalSent: 'Emails Sent',
  updated: 'Last Updated',
};

function generatePerProfileLimitData(data: Data) {
  return {
    'Include in Message Limit': data.model!.useMessageLimits,
  };
}

function generateRateLimitData(data: Data) {
  let payload: Record<string, string | number> = {};

  if (data.model?.rateLimitEmailRate) {
    payload['Send Rate Message Count'] = data.model.rateLimitEmailRate;
  }
  if (data.model?.rateLimitTimePeriod) {
    payload['Send Rate Interval'] = SECONDS_TO_DATE_UNIT[data.model.rateLimitTimePeriod];
  }

  return payload;
}

function generateNewsletterData(data: Data) {
  return {
    'Newsletter ID': data.model!.id,
    'Newsletter Channel': data.model!.channel,
  };
}

function generateTriggeredBroadcastData(data: Data) {
  return {
    'Campaign ID': data.model!.id,
  };
}

function generateBroadcastData(data: Data) {
  if (data.model!.isApiTriggered) {
    return generateTriggeredBroadcastData(data);
  } else {
    return generateNewsletterData(data);
  }
}

function generateNumberOfConditionsData(data: Data) {
  let { attributeConditions, newsletterConditions, messageCondition } = data.model!.filters.reduce(
    (
      acc: { segment: number; attributeConditions: number; newsletterConditions: number; messageCondition: number },
      val: any,
    ) => {
      switch (val.type) {
        case 'attribute':
          acc.attributeConditions += 1;
          break;
        case 'newsletter_recipients':
          acc.newsletterConditions += 1;
          break;
        case 'message':
          acc.messageCondition += 1;
          break;
      }

      return acc;
    },
    { attributeConditions: 0, newsletterConditions: 0, messageCondition: 0 },
  );

  let { segmentConditions, objectSegmentConditions } = data
    .model!.hasMany('segments')
    .value()
    .toArray()
    .reduce(
      (acc: { segmentConditions: number; objectSegmentConditions: number }, segmentModel: SegmentModel) => {
        // If segment has a relationship condition, that means it uses an object
        if (JSON.stringify(segmentModel.conditions).includes('"relationship":')) {
          acc.objectSegmentConditions += 1;
          return acc;
        }

        acc.segmentConditions += 1;

        return acc;
      },
      { segmentConditions: 0, objectSegmentConditions: 0 },
    );

  return {
    'Number of Segment Conditions': segmentConditions,
    'Number of Object Segment Conditions': objectSegmentConditions,
    'Number of Attribute Conditions': attributeConditions,
    'Number of Newsletter Conditions': newsletterConditions,
    'Number of Message Conditions': messageCondition,
  };
}

type NewsletterSetupTab = 'recipients' | 'content' | 'tracking' | 'review';

const Transforms: TransformsList = {
  listView(data) {
    let type;

    if (data.route!.name.includes('broadcasts.list.newsletters')) {
      type = 'Newsletter';
    } else {
      type = 'Triggered Broadcast';
    }

    let tags: false | string[] = false;
    let objectTypes: false | string[] = false;

    if (data.route != null) {
      tags = getModelEntryNameFromId(data.route.queryParams.tagIds, data.tags);
      objectTypes = getModelEntryNameFromId(data.route.queryParams.objectTypeIds, data.objectTypes);
    }

    return {
      'Broadcast Type': type,
      ...(data.route != null && { 'Filtered By Broadcast Search': isPresent(data.route.queryParams.search) }),
      ...(tags !== false && { 'Filtered By Campaign Tag(s)': tags }),
      ...(objectTypes !== false && { 'Filtered by Object Types': objectTypes }),
    };
  },

  base(data) {
    return generateBroadcastData(data);
  },

  newsletter(data) {
    return generateNewsletterData(data);
  },

  newsletterCopyStarted(data) {
    return {
      ...generateNewsletterData(data),
      'Languages Count': data.model!.languageVariations.length,
    };
  },

  immediateSend(data) {
    return {
      ...generateNewsletterData(data),
      ...generateRateLimitData(data),
      ...generatePerProfileLimitData(data),
      ...generateNumberOfConditionsData(data),
      'Scheduling or Immediate Sending': 'Immediate Sending',
    };
  },

  scheduledSend(data) {
    return {
      ...generateNewsletterData(data),
      ...generateRateLimitData(data),
      ...generatePerProfileLimitData(data),
      ...generateNumberOfConditionsData(data),
      'Scheduling or Immediate Sending': 'Scheduling',
    };
  },

  reviewTabViewed(data: Data & { languageWarningVisible: boolean }) {
    let isAPITB = data.model!.isApiTriggered;

    return {
      ...generateBroadcastData(data),
      'Broadcast Type': isAPITB ? 'Triggered Broadcast' : 'Newsletter',
      'Language Warning Visible': !isAPITB ? data.model!.templateLanguageVariationsDefaulted : undefined,
    };
  },

  baseWithType(data) {
    return {
      ...generateBroadcastData(data),
      'Broadcast Type': data.model!.isApiTriggered ? 'Triggered Broadcast' : 'Newsletter',
    };
  },

  baseWithSent(data) {
    // Newsletters have a sentAt attr but API triggered broadcasts do not. For
    // them, broadcastTrigger indicates how many times it's been sent.
    let sent = data.model!.isApiTriggered ? get(data.model!, 'broadcastTrigger.length') > 0 : data.model!.sentAt;

    return {
      ...generateBroadcastData(data),
      'Broadcast Type': data.model!.isApiTriggered ? 'Triggered Broadcast' : 'Newsletter',
      'Broadcast Sent Or Not': sent ? 'Sent' : 'Not Sent',
    };
  },

  newsletterWithSent(data) {
    return {
      ...generateNewsletterData(data),
      'Broadcast Sent Or Not': data.model!.sentAt ? 'Sent' : 'Not Sent',
    };
  },

  editDiscard(data: Data & { from: NewsletterSetupTab }) {
    return {
      ...generateNewsletterData(data),
      'Edit(s) Discarded On': {
        recipients: 'Recipients Tab',
        content: 'Content Tab',
        tracking: 'Goal Tab',
        review: 'Review Tab',
      }[data.from],
    };
  },

  editStarted(data: Data & { editType: BroadcastEditType; channelChangedTo: ChannelType }) {
    return {
      ...generateNewsletterData(data),
      'Newsletter Channel Changed To': data?.channelChangedTo,
      'Newsletter Content ID': data?.model?.templates?.get('firstObject').id,
      'Broadcast Edit Type': BROADCAST_EDIT_TYPES[data?.editType],
      'Broadcast Scheduled or Unscheduled': data?.model?.isScheduled ? 'Scheduled' : 'Unscheduled',
    };
  },

  newsletterTestTabViewed(data: Data) {
    return generateBroadcastData(data);
  },

  listSorted(data: Data & { sort: BroadcastSortOption }) {
    return {
      sorting: BROADCAST_SORT_OPTIONS[data.sort],
    };
  },
};

export default Transforms;
